/**
 * "What grade are you in?": asked once, after a new player types their name
 * (and once of players from before it existed), then on to the helpers page.
 *
 * The answer sets the player's word ramp, never their starting level:
 * everyone plays all 100 levels (docs/WORDS_AND_SENTENCES.md, part 3).
 * Grades 3 and 4 also start with keyboard hints on; the helpers page that
 * follows can turn them off again. A grown-up can change the grade in
 * Settings.
 */
import Phaser from 'phaser';
import { drawVerticalGradient, MENU_GRADIENT } from '../art/backdrop';
import { setDevState } from '../systems/devQuery';
import { GRADE_BANDS } from '../systems/WordPicker';
import type { SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import type { GradeBand } from '../types';
import { pressButton, styleButton } from './ui/menuButton';

/** Grades that start with keyboard hints on. */
const HINTS_ON_GRADES: readonly GradeBand[] = [3, 4];

const ORDINALS: Readonly<Record<GradeBand, string>> = {
  3: '3rd',
  4: '4th',
  5: '5th',
  6: '6th',
  7: '7th',
  8: '8th',
};

export function gradeLabel(grade: GradeBand | null): string {
  return grade === null ? 'Not sure' : `${ORDINALS[grade]} grade`;
}

export class Grade extends Phaser.Scene {
  private save!: SaveManager;
  private buttons: Phaser.GameObjects.Text[] = [];
  private choices: Array<GradeBand | null> = [];
  private focused = 0;

  constructor() {
    super('Grade');
  }

  create(): void {
    setDevState({ scene: 'Grade' });
    this.save = this.registry.get('save') as SaveManager;
    this.buttons = [];
    this.choices = [...GRADE_BANDS, null];
    drawVerticalGradient(this, MENU_GRADIENT);

    this.add
      .text(GAME_WIDTH / 2, IS_PORTRAIT ? 150 : 90, 'What grade are you in?', {
        fontFamily: FONT_FAMILY,
        fontSize: IS_PORTRAIT ? '46px' : '52px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 6,
      })
      .setOrigin(0.5);
    this.add
      .text(
        GAME_WIDTH / 2,
        IS_PORTRAIT ? 230 : 156,
        'It picks words that are right for you. You still get every level.',
        {
          fontFamily: FONT_FAMILY,
          fontSize: '22px',
          color: PALETTE.ice,
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 80 },
        },
      )
      .setOrigin(0.5);

    // Wide stage: two rows of three grades, "Not sure" beneath. Tall: a column.
    this.choices.forEach((grade, index) => {
      const wide = !IS_PORTRAIT;
      const x = wide && grade !== null ? GAME_WIDTH / 2 + ((index % 3) - 1) * 300 : GAME_WIDTH / 2;
      const y = wide
        ? grade === null
          ? 560
          : 290 + Math.floor(index / 3) * 120
        : 330 + index * 100;
      const button = this.add
        .text(x, y, gradeLabel(grade), {
          fontFamily: FONT_FAMILY,
          fontSize: grade === null ? '28px' : '36px',
          color: PALETTE.snow,
          backgroundColor: '#1b2a41',
          padding: { x: 28, y: 14 },
        })
        .setOrigin(0.5)
        .setFixedSize(IS_PORTRAIT ? GAME_WIDTH - 160 : 260, 0)
        .setAlign('center')
        .setInteractive({ useHandCursor: true });
      button.on('pointerover', () => this.focus(index));
      button.on('pointerup', () => pressButton(button, () => this.choose(grade)));
      this.buttons.push(button);
    });

    this.installInput();
    // Start on the player's grade if they have one, else on "Not sure".
    const current = this.save.grade();
    this.focus(Math.max(0, this.choices.indexOf(current)));
  }

  private choose(grade: GradeBand | null): void {
    this.save.setGrade(grade);
    const firstTime = this.save.activeProfile()?.seenHelpers === false;
    if (firstTime && grade !== null && HINTS_ON_GRADES.includes(grade)) {
      this.save.setSetting('keyboardHints', true);
    }
    this.scene.start('Helpers');
  }

  private focus(index: number): void {
    this.focused = index;
    this.buttons.forEach((button, i) => styleButton(button, i === index));
  }

  private installInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      const count = this.buttons.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        this.focus((this.focused + count - 1) % count);
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        this.focus((this.focused + 1) % count);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const button = this.buttons[this.focused];
        if (button !== undefined)
          pressButton(button, () => this.choose(this.choices[this.focused] ?? null));
      } else if (event.key === 'Escape') {
        this.choose(this.save.grade());
      }
    });
  }
}
